import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AuthLayout from '../layouts/AuthLayout';
import Layout from '../layouts/Layout';
import useAuth from '../hooks/auth/useAuth';
import useApiClient from '../hooks/api/useApiClient';
import useQueryParams from '../hooks/utility/useQueryParams';
import LoadingScreen from '../components/loading/LoadingScreen';
import { ScreenEnum } from '../hooks/navigation/Screens';
import useHostAccessGuard from '../hooks/auth/useHostAccessGuard';
import { rememberHostAccessRefusal } from '../services/auth/hostAccessRefusal';

const loadingView = (
  <div className="pt-4 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

export default function Router() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Navigation:Router' });

  const { isLoggedIn, logIn } = useAuth();

  const query = useQueryParams();
  const { config } = useApiClient();
  const checkHostAccess = useHostAccessGuard();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    query.has('auth_token') && validateToken();
  }, []);

  async function validateToken() {
    setLoading(true);
    const token = query.get('auth_token');
    const { success } = await axios
      .create(config)
      .request({
        method: 'GET',
        url: '/initial-data',
        headers: {
          Authorization: `Bearer ${token}`,
          // The tenant headers in `config` belong to the session already open in this
          // browser (another partner's restaurant, say). Sent with the new token they
          // name a tenant it cannot access and the check fails; blank them as a
          // logged-out browser would. logIn then resets the old tenant state.
          'x-on-behalf-of-company': '',
          'x-on-behalf-of-franchise': '',
          'x-on-behalf-of-service-provider': '',
        },
      })
      .then(() => ({ success: true }))
      .catch(() => ({ success: false }));

    if (!success) {
      setLoading(false);

      return toast.error(t('toasts.auth_token_invalid'));
    }

    // Magic links are minted for the account's own address, so this only refuses a link
    // opened on a partner address the account is not part of — sign in on the right one.
    // The login screen that renders next picks the refusal up and shows the account's own
    // sign-in link, as the sign-in form does; the toast is only the immediate answer.
    const access = await checkHostAccess(token);

    if (!access.allowed) {
      rememberHostAccessRefusal(access);
      setLoading(false);

      return toast.error(t('toasts.host_access_refused'));
    }

    return onLogin(token);
  }

  async function onLogin(token) {
    logIn({
      data: { access_token: token },
    });

    setTimeout(() => window.location.replace(ScreenEnum.Home), 5);
  }

  function renderRoutes() {
    if (!isLoggedIn) {
      return <AuthLayout />;
    }

    return <Layout />;
  }

  return (
    <BrowserRouter>
      {loading ? <LoadingScreen /> : <React.Suspense fallback={loadingView}>{renderRoutes()}</React.Suspense>}
    </BrowserRouter>
  );
}
